import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './SolutionTemplate.scss';

const TAG_COLORS = {
    blue:   { bg: 'rgba(26, 111, 181, 0.15)',  border: 'rgba(59, 154, 238, 0.35)',  text: '#3b9aee' },
    green:  { bg: 'rgba(5, 150, 105, 0.15)',   border: 'rgba(16, 185, 129, 0.35)',  text: '#10b981' },
    purple: { bg: 'rgba(124, 58, 237, 0.15)',  border: 'rgba(167, 139, 250, 0.35)', text: '#a78bfa' },
};

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay }}
        >
            {children}
        </motion.div>
    );
}

export default function SolutionTemplate({
    tag         = '',
    tagVariant  = 'blue',
    heroIcon,
    title       = '',
    subtitle    = '',
    stats       = [],
    features    = [],
    steps       = [],
    accentColor = '#1a6fb5',
    ctaTitle    = '',
    ctaSubtitle = '',
    ctaButton   = 'Get Started',
    ctaPath     = '/login',
}) {
    const navigate = useNavigate();
    const cn = 'solutionTemplate';
    const tagStyle = TAG_COLORS[tagVariant] || TAG_COLORS.blue;

    const seo = useSEO({
        title:       `${title}`,
        description: subtitle.length > 160 ? subtitle.slice(0, 157) + '...' : subtitle,
        schema: {
            '@context': 'https://schema.org',
            '@type':    'WebPage',
            name:       title,
            description: subtitle,
            breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Home',      item: 'https://resolve.kaiziq.io/' },
                    { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://resolve.kaiziq.io/#/solutions' },
                    { '@type': 'ListItem', position: 3, name: title },
                ],
            },
        },
    });

    const handleCta = () => {
        if (ctaPath) navigate(ctaPath);
    };

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            {/* ── Hero ──────────────────────────────────────────────── */}
            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div
                        className={cn + '__hero__tag'}
                        style={{ background: tagStyle.bg, border: `1px solid ${tagStyle.border}`, color: tagStyle.text }}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                    >
                        {tag}
                    </motion.div>

                    <motion.div
                        className={cn + '__hero__icon'}
                        style={{ background: `${accentColor}22`, border: `1px solid ${accentColor}44`, color: accentColor }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.45, delay: 0.05 }}
                    >
                        {heroIcon}
                    </motion.div>

                    <motion.h1
                        className={cn + '__hero__title'}
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                    >
                        {title}
                    </motion.h1>

                    <motion.p
                        className={cn + '__hero__sub'}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.18 }}
                    >
                        {subtitle}
                    </motion.p>

                    <motion.div
                        className={cn + '__hero__actions'}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.26 }}
                    >
                        <button className={cn + '__hero__cta'} onClick={handleCta}>
                            {ctaButton} <ArrowRight size={16} />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────────── */}
            {stats.length > 0 && (
                <section className={cn + '__stats'}>
                    {stats.map((stat, i) => (
                        <Reveal key={i} className={cn + '__stats__item'} delay={i * 0.1}>
                            <div className={cn + '__stats__value'} style={{ color: accentColor }}>
                                {stat.value}
                            </div>
                            <div className={cn + '__stats__label'}>{stat.label}</div>
                        </Reveal>
                    ))}
                </section>
            )}

            {/* ── Features ──────────────────────────────────────────── */}
            {features.length > 0 && (
                <section className={cn + '__features'}>
                    <Reveal>
                        <div className={cn + '__section-head'}>
                            <h2 className={cn + '__section-title'}>Everything your team needs</h2>
                            <p className={cn + '__section-sub'}>
                                Purpose-built capabilities that solve the real challenges in this space.
                            </p>
                        </div>
                    </Reveal>
                    <div className={cn + '__features__grid'}>
                        {features.map((f, i) => (
                            <Reveal key={i} className={cn + '__feature'} delay={i * 0.07}>
                                <div
                                    className={cn + '__feature__icon'}
                                    style={{ background: `${accentColor}18`, border: `1px solid ${accentColor}30`, color: accentColor }}
                                >
                                    {f.icon}
                                </div>
                                <h4 className={cn + '__feature__title'}>{f.title}</h4>
                                <p className={cn + '__feature__desc'}>{f.desc}</p>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {/* ── How it works ──────────────────────────────────────── */}
            {steps.length > 0 && (
                <section className={cn + '__steps'}>
                    <Reveal>
                        <div className={cn + '__section-head'}>
                            <h2 className={cn + '__section-title'}>How it works</h2>
                            <p className={cn + '__section-sub'}>Three steps from sign-up to fully operational.</p>
                        </div>
                    </Reveal>
                    <div className={cn + '__steps__grid'}>
                        {steps.map((s, i) => (
                            <Reveal key={i} className={cn + '__step'} delay={i * 0.12}>
                                <div className={cn + '__step__num'} style={{ color: accentColor }}>
                                    {s.step}
                                </div>
                                <h4 className={cn + '__step__title'}>{s.title}</h4>
                                <p className={cn + '__step__desc'}>{s.desc}</p>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {/* ── CTA Banner ────────────────────────────────────────── */}
            <section className={cn + '__cta'}>
                <Reveal className={cn + '__cta__inner'}>
                    <h2 className={cn + '__cta__title'}>{ctaTitle}</h2>
                    <p className={cn + '__cta__sub'}>{ctaSubtitle}</p>
                    <button className={cn + '__cta__btn'} onClick={handleCta}>
                        {ctaButton} <ArrowRight size={16} />
                    </button>
                </Reveal>
            </section>

            <Footer />
        </div>
    );
}
